import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Badge,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Inventory as ProductIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Category as CategoryIcon,
  LocalOffer as PriceIcon,
  QrCode2 as BarcodeIcon,
  ShoppingCart as CartIcon,
} from "@mui/icons-material";

const ProductMaster = () => {
  const [productData, setProductData] = useState([
    { 
      id: 1, 
      productCode: "PROD001", 
      productName: "Premium Laptop", 
      category: "Electronics",
      subCategory: "Computers",
      brand: "Dell",
      price: 75000,
      stock: 25,
      unit: "Pcs",
      status: "Active",
      description: "High-performance laptop with latest specifications"
    },
    { 
      id: 2, 
      productCode: "PROD002", 
      productName: "Wireless Mouse", 
      category: "Electronics",
      subCategory: "Accessories",
      brand: "Logitech",
      price: 1200,
      stock: 150,
      unit: "Pcs",
      status: "Active",
      description: "Ergonomic wireless mouse with long battery life"
    },
    { 
      id: 3, 
      productCode: "PROD003", 
      productName: "Office Chair", 
      category: "Furniture",
      subCategory: "Seating",
      brand: "Featherlite",
      price: 8500,
      stock: 40,
      unit: "Pcs",
      status: "Active",
      description: "Comfortable ergonomic office chair"
    },
    { 
      id: 4, 
      productCode: "PROD004", 
      productName: "LED Monitor 24\"", 
      category: "Electronics",
      subCategory: "Displays",
      brand: "Samsung",
      price: 12000,
      stock: 0,
      unit: "Pcs",
      status: "Out of Stock",
      description: "Full HD LED monitor with vibrant display"
    },
    { 
      id: 5, 
      productCode: "PROD005", 
      productName: "Office Desk", 
      category: "Furniture",
      subCategory: "Tables",
      brand: "Godrej",
      price: 15000,
      stock: 18,
      unit: "Pcs",
      status: "Active",
      description: "Spacious office desk with storage"
    },
    { 
      id: 6, 
      productCode: "PROD006", 
      productName: "Keyboard & Mouse Combo", 
      category: "Electronics",
      subCategory: "Accessories",
      brand: "HP",
      price: 2500,
      stock: 80,
      unit: "Set",
      status: "Active",
      description: "Wireless keyboard and mouse combo"
    },
    { 
      id: 7, 
      productCode: "PROD007", 
      productName: "USB Hub 4-Port", 
      category: "Electronics",
      subCategory: "Accessories",
      brand: "TP-Link",
      price: 850,
      stock: 5,
      unit: "Pcs",
      status: "Low Stock",
      description: "USB 3.0 hub with 4 ports"
    },
    { 
      id: 8, 
      productCode: "PROD008", 
      productName: "Printer Multifunction", 
      category: "Electronics",
      subCategory: "Printers",
      brand: "Canon",
      price: 18500,
      stock: 12,
      unit: "Pcs",
      status: "Active",
      description: "All-in-one multifunction printer"
    },
    { 
      id: 9, 
      productCode: "PROD009", 
      productName: "Filing Cabinet", 
      category: "Furniture",
      subCategory: "Storage",
      brand: "Godrej",
      price: 9500,
      stock: 22,
      unit: "Pcs",
      status: "Active",
      description: "4-drawer filing cabinet"
    },
    { 
      id: 10, 
      productCode: "PROD010", 
      productName: "Webcam HD", 
      category: "Electronics",
      subCategory: "Accessories",
      brand: "Logitech",
      price: 3500,
      stock: 0,
      unit: "Pcs",
      status: "Out of Stock",
      description: "1080p HD webcam with microphone"
    },
  ]);

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [successMessage, setSuccessMessage] = useState("");
  const [errors, setErrors] = useState({});

  // Modal state
  const [openModal, setOpenModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [viewingProduct, setViewingProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [newProduct, setNewProduct] = useState({
    productCode: "",
    productName: "",
    category: "",
    subCategory: "",
    brand: "",
    price: "",
    stock: "",
    unit: "Pcs",
    status: "Active",
    description: "",
  });

  // Get unique categories for filter
  const categories = [...new Set(productData.map(p => p.category))];

  // Helper functions
  const getFilteredProducts = () => {
    return productData.filter((product) => {
      const matchesSearch = Object.values(product).some((value) =>
        String(value).toLowerCase().includes(searchQuery.toLowerCase())
      );
      const matchesStatus = statusFilter === "all" || product.status === statusFilter;
      const matchesCategory = categoryFilter === "all" || product.category === categoryFilter;
      return matchesSearch && matchesStatus && matchesCategory;
    });
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Active':
        return { backgroundColor: '#e8f5e9', color: '#2e7d32' };
      case 'Out of Stock':
        return { backgroundColor: '#ffebee', color: '#d32f2f' };
      case 'Low Stock':
        return { backgroundColor: '#fff3e0', color: '#e65100' };
      case 'Inactive':
        return { backgroundColor: '#f5f5f5', color: '#666' };
      default:
        return { backgroundColor: '#f5f5f5', color: '#666' };
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!newProduct.productCode.trim()) newErrors.productCode = 'Product code is required';
    if (!newProduct.productName.trim()) newErrors.productName = 'Product name is required';
    if (!newProduct.category.trim()) newErrors.category = 'Category is required';
    if (!newProduct.brand.trim()) newErrors.brand = 'Brand is required';
    if (!newProduct.price || newProduct.price <= 0) newErrors.price = 'Price must be greater than 0';
    if (!newProduct.stock || newProduct.stock < 0) newErrors.stock = 'Stock cannot be negative';
    return newErrors;
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Handlers
  const handleEdit = (product) => {
    setEditingProduct(product);
    setNewProduct({ ...product });
    setOpenModal(true);
  };

  const handleView = (product) => {
    setViewingProduct(product);
    setOpenViewModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      setProductData(productData.filter((product) => product.id !== id));
      setSuccessMessage("Product deleted successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    }
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Search and filter handlers
  const handleSearch = () => {
    setPage(0);
    setSuccessMessage(`Search completed. Found ${getFilteredProducts().length} products.`);
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const handleReset = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setCategoryFilter("all");
    setPage(0);
    setErrors({});
    setSuccessMessage("");
  };

  const handleOpenModal = () => {
    setEditingProduct(null);
    setNewProduct({
      productCode: "",
      productName: "",
      category: "",
      subCategory: "",
      brand: "",
      price: "",
      stock: "",
      unit: "Pcs",
      status: "Active",
      description: "",
    });
    setErrors({});
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingProduct(null);
    setErrors({});
  };

  const handleCloseViewModal = () => {
    setOpenViewModal(false);
    setViewingProduct(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewProduct({ ...newProduct, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const handleAddProduct = async () => {
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setIsLoading(true);
    try {
      if (editingProduct) {
        // Update existing product
        setProductData(productData.map(product => 
          product.id === editingProduct.id 
            ? { 
                ...product, 
                ...newProduct, 
                price: parseFloat(newProduct.price),
                stock: parseInt(newProduct.stock)
              }
            : product
        ));
        setSuccessMessage("Product updated successfully!");
      } else {
        // Add new product
        const newId = productData.length > 0 ? Math.max(...productData.map(p => p.id)) + 1 : 1;
        setProductData([...productData, { 
          id: newId, 
          ...newProduct, 
          price: parseFloat(newProduct.price),
          stock: parseInt(newProduct.stock)
        }]);
        setSuccessMessage("Product added successfully!");
      }
      handleCloseModal();
    } catch (error) {
      setErrors({ general: "An error occurred. Please try again." });
    } finally {
      setIsLoading(false);
      setTimeout(() => setSuccessMessage(""), 3000);
    }
  };

  const filteredProducts = getFilteredProducts();

  // Calculate statistics
  const totalProducts = productData.length;
  const activeProducts = productData.filter(p => p.status === "Active").length;
  const outOfStock = productData.filter(p => p.status === "Out of Stock").length;
  const lowStock = productData.filter(p => p.status === "Low Stock").length;
  const totalValue = productData.reduce((sum, product) => sum + (product.price * product.stock), 0);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Badge badgeContent={totalProducts} color="primary">
            <ProductIcon sx={{ color: 'primary.main', fontSize: 40 }} />
          </Badge>
          <Box>
            <Typography variant="h4" fontWeight="bold" color="primary">
              Product Master Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage your retail product inventory • {filteredProducts.length} products found
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenModal}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            fontWeight: 600,
            backgroundColor: 'primary.main',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
          }}
        >
          Add New Product
        </Button>
      </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Statistics Cards */}
      {/* <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <ProductIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h6" fontWeight="bold" color="primary">
                {totalProducts}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Products
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <CartIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h6" fontWeight="bold" color="success.main">
                {activeProducts}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Products
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <InfoIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
              <Typography variant="h6" fontWeight="bold" color="warning.main">
                {lowStock}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Low Stock Items
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <InfoIcon sx={{ fontSize: 40, color: 'error.main', mb: 1 }} />
              <Typography variant="h6" fontWeight="bold" color="error.main">
                {outOfStock}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Out of Stock
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <PriceIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
              <Typography variant="h6" fontWeight="bold" color="info.main">
                {formatCurrency(totalValue)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Inventory Value
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid> */}

      {/* Search and Filter Section */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Search Products..."
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2.5}>
            <TextField
              select
              fullWidth
              label="Filter by Category"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              SelectProps={{
                native: true,
              }}
            >
              <option value="all">All Categories</option>
              {categories.map((category) => (
                <option key={category} value={category}>{category}</option>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2.5}>
            <TextField
              select
              fullWidth
              label="Filter by Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              SelectProps={{
                native: true,
              }}
            >
              <option value="all">All Status</option>
              <option value="Active">Active</option>
              <option value="Low Stock">Low Stock</option>
              <option value="Out of Stock">Out of Stock</option>
              <option value="Inactive">Inactive</option>
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleSearch}
                startIcon={<SearchIcon />}
                sx={{ flex: 1 }}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                onClick={handleReset}
                startIcon={<RefreshIcon />}
                sx={{ flex: 1 }}
              >
                Reset
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Table Section */}
      <Paper sx={{ borderRadius: 2, boxShadow: 3, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>S.No</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Product Code</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Product Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Brand</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Price</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Stock</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Unit</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredProducts.length > 0 ? (
                filteredProducts
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((product, index) => (
                    <TableRow 
                      key={product.id} 
                      sx={{ 
                        '&:hover': { backgroundColor: '#f5f5f5' },
                        transition: 'background-color 0.2s'
                      }}
                    >
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <BarcodeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                          {product.productCode}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{product.productName}</TableCell>
                      <TableCell>
                        <Chip 
                          label={product.category} 
                          size="small" 
                          icon={<CategoryIcon />}
                          sx={{ 
                            backgroundColor: 'primary.light', 
                            color: 'primary.dark',
                            fontWeight: 500
                          }} 
                        />
                      </TableCell>
                      <TableCell>{product.brand}</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>
                        {formatCurrency(product.price)}
                      </TableCell>
                      <TableCell>
                        <Typography 
                          sx={{ 
                            fontWeight: 600,
                            color: product.stock === 0 ? 'error.main' : product.stock < 10 ? 'warning.main' : 'success.main'
                          }}
                        >
                          {product.stock}
                        </Typography>
                      </TableCell>
                      <TableCell>{product.unit}</TableCell>
                      <TableCell>
                        <Chip
                          label={product.status}
                          size="small"
                          sx={{
                            ...getStatusStyle(product.status),
                            fontWeight: 600,
                            fontSize: '0.75rem'
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleView(product)}
                              sx={{
                                color: 'info.main',
                                '&:hover': {
                                  backgroundColor: 'info.light',
                                  transform: 'scale(1.1)',
                                },
                                transition: 'all 0.2s'
                              }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Product">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(product)}
                              sx={{
                                color: 'warning.main',
                                '&:hover': {
                                  backgroundColor: 'warning.light',
                                  transform: 'scale(1.1)',
                                },
                                transition: 'all 0.2s'
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Product">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(product.id)}
                              sx={{
                                color: 'error.main',
                                '&:hover': {
                                  backgroundColor: 'error.light',
                                  transform: 'scale(1.1)',
                                },
                                transition: 'all 0.2s'
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
              ) : (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <ProductIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">
                      No products found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Try adjusting your search criteria or add a new product.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredProducts.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Add/Edit Product Modal */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{
          backgroundColor: 'primary.main',
          color: 'white',
          padding: '16px 24px',
        }}>
          <Typography variant="h6" component="div" sx={{ color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
            <ProductIcon />
            {editingProduct ? 'Edit Product' : 'Add New Product'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ padding: '24px', mt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Product Code"
                name="productCode"
                value={newProduct.productCode}
                onChange={handleInputChange}
                error={!!errors.productCode}
                helperText={errors.productCode}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BarcodeIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Product Name"
                name="productName"
                value={newProduct.productName}
                onChange={handleInputChange}
                error={!!errors.productName}
                helperText={errors.productName}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.category}>
                <InputLabel>Category *</InputLabel>
                <Select
                  name="category"
                  value={newProduct.category}
                  onChange={handleInputChange}
                  label="Category *"
                >
                  <MenuItem value="Electronics">Electronics</MenuItem>
                  <MenuItem value="Furniture">Furniture</MenuItem>
                  <MenuItem value="Stationery">Stationery</MenuItem>
                  <MenuItem value="Accessories">Accessories</MenuItem>
                  <MenuItem value="Hardware">Hardware</MenuItem>
                  <MenuItem value="Software">Software</MenuItem>
                </Select>
                {errors.category && <Typography variant="caption" color="error">{errors.category}</Typography>}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Sub Category"
                name="subCategory"
                value={newProduct.subCategory}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Brand"
                name="brand"
                value={newProduct.brand}
                onChange={handleInputChange}
                error={!!errors.brand}
                helperText={errors.brand}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Price"
                name="price"
                type="number"
                value={newProduct.price}
                onChange={handleInputChange}
                error={!!errors.price}
                helperText={errors.price}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PriceIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Stock Quantity"
                name="stock"
                type="number"
                value={newProduct.stock}
                onChange={handleInputChange}
                error={!!errors.stock}
                helperText={errors.stock}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Unit</InputLabel>
                <Select
                  name="unit"
                  value={newProduct.unit}
                  onChange={handleInputChange}
                  label="Unit"
                >
                  <MenuItem value="Pcs">Pieces</MenuItem>
                  <MenuItem value="Set">Set</MenuItem>
                  <MenuItem value="Box">Box</MenuItem>
                  <MenuItem value="Kg">Kilogram</MenuItem>
                  <MenuItem value="Ltr">Liter</MenuItem>
                  <MenuItem value="Mtr">Meter</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={newProduct.status}
                  onChange={handleInputChange}
                  label="Status"
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                  <MenuItem value="Low Stock">Low Stock</MenuItem>
                  <MenuItem value="Out of Stock">Out of Stock</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={newProduct.description}
                onChange={handleInputChange}
                multiline
                rows={3}
                placeholder="Enter product description..."
              />
            </Grid>
          </Grid>
          {errors.general && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errors.general}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button onClick={handleCloseModal} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddProduct}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : <AddIcon />}
            sx={{
              backgroundColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
            }}
          >
            {isLoading ? 'Processing...' : (editingProduct ? 'Update Product' : 'Add Product')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Product Modal */}
      <Dialog open={openViewModal} onClose={handleCloseViewModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{
          backgroundColor: 'primary.main',
          color: 'white',
          padding: '16px 24px',
        }}>
          <Typography variant="h6" component="div" sx={{ color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon />
            Product Details
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ padding: '24px', mt: 2 }}>
          {viewingProduct && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Avatar 
                    sx={{ 
                      width: 80, 
                      height: 80, 
                      margin: '0 auto',
                      backgroundColor: 'primary.main',
                    }}
                  >
                    <ProductIcon sx={{ fontSize: 40 }} />
                  </Avatar>
                  <Typography variant="h5" sx={{ mt: 2, fontWeight: 'bold' }}>
                    {viewingProduct.productName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {viewingProduct.productCode}
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Category</Typography>
                  <Typography variant="body1" fontWeight="bold">{viewingProduct.category}</Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Sub Category</Typography>
                  <Typography variant="body1" fontWeight="bold">{viewingProduct.subCategory}</Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Brand</Typography>
                  <Typography variant="body1" fontWeight="bold">{viewingProduct.brand}</Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Price</Typography>
                  <Typography variant="body1" fontWeight="bold" color="primary">
                    {formatCurrency(viewingProduct.price)}
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Stock</Typography>
                  <Typography 
                    variant="body1" 
                    fontWeight="bold"
                    sx={{ 
                      color: viewingProduct.stock === 0 ? 'error.main' : viewingProduct.stock < 10 ? 'warning.main' : 'success.main'
                    }}
                  >
                    {viewingProduct.stock} {viewingProduct.unit}
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      label={viewingProduct.status}
                      sx={{
                        ...getStatusStyle(viewingProduct.status),
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                </Paper>
              </Grid>
              
              <Grid item xs={12}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Description</Typography>
                  <Typography variant="body1" sx={{ mt: 1 }}>
                    {viewingProduct.description || 'No description available'}
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(144, 19, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Inventory Value</Typography>
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    {formatCurrency(viewingProduct.price * viewingProduct.stock)}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button onClick={handleCloseViewModal}>Close</Button>
          {viewingProduct && (
            <Button
              variant="contained"
              onClick={() => {
                handleCloseViewModal();
                handleEdit(viewingProduct);
              }}
              startIcon={<EditIcon />}
              sx={{
                backgroundColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              }}
            >
              Edit Product
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductMaster;

